import { RouterConfigOptions, Routes } from '@angular/router'

import { assessmentSettingsGuard } from './guards/assessment-settings.guard'
import { authGuard } from './guards/auth.guard'
import { licenseGuard } from './guards/license.guard'
import { meetingRecordingGuard } from './guards/meeting-recording.guard'
import { roleGuard } from './guards/role.guard'
import { AssessmentSettingsPageComponent } from './pages/admin/assessment-settings-page/assessment-settings-page.component'
import { LicenseUsagePageComponent } from './pages/admin/license-usage-page/license-usage-page.component'
import { OrganizationSettingPageComponent } from './pages/admin/organization-setting-page/organization-setting-page.component'
import { AdminUserListComponent } from './pages/admin/user/list/admin-user-list.component'
import { ExternalIntegrationPageComponent } from './pages/admin/external-integration-page/external-integration.component'
import { HomeComponent } from './pages/assessment/home/home.component'
import { AssessmentListPageComponent } from './pages/assessment-list-page/assessment-list-page.component'
import { AuthActionComponent } from './pages/auth_action/auth-action.component'
import { ForgotPasswordPageComponent } from './pages/forgot-password-page/forgot-password-page.component'
import { LoginPageComponent } from './pages/login-page/login-page.component'
import { MainPageComponent } from './pages/main-page/main-page.component'
import { NoLicensePageComponent } from './pages/no-license-page/no-license-page.component'
import { UserListPageComponent } from './pages/user-list-page/user-list-page.component'
import { IndividualComponent } from './pages/dashboard/individual/individual.component'
import { AssessmentBulkExecutionComponent } from './pages/assessment/bulk-execution/bulk-execution.component'
import { OrganizationComponent } from './pages/dashboard/organization/organization.component'
import { SalesforceAuthComponent } from './pages/salesforce-auth/salesforce-auth.component'
import { MfaPhoneSetupPageComponent } from './pages/mfa-phone-setup-page/mfa-phone-setup-page.component'
import { TotpVerificationPageComponent } from './pages/totp-verification-page/totp-verification-page.component'
import { MfaVerifySelectPageComponent } from './pages/mfa-verify-select-page/mfa-verify-select-page.component'
import { MfaMethodSelectPageComponent } from './pages/mfa-method-select-page/mfa-method-select-page.component'
import { PasskeySetupPageComponent } from './pages/passkey-setup-page/passkey-setup-page.component'
import { PasskeyVerifyPageComponent } from './pages/passkey-verify-page/passkey-verify-page.component'
import { MfaTotpSetupPageComponent } from './pages/mfa-totp-setup-page/mfa-totp-setup-page.component'
import { MfaSettingsPageComponent } from './pages/settings/mfa-settings-page/mfa-settings-page.component'
import { SmsVerificationPageComponent } from './pages/sms-verification-page/sms-verification-page.component'
import { PersonaListPageComponent } from './pages/roleplay/persona/list/persona-list-page.component'
import { RoleplaySessionPageComponent } from './pages/roleplay/session/roleplay-session-page.component'
import { addonGuard } from './guards/addon.guard'
import { roleplayStreamingGuard } from './guards/roleplay-streaming.guard'
import type { StreamingSessionPageComponent } from './pages/roleplay/streaming-session/streaming-session-page.component'
import {
  ROLEPLAY_SOUND_EFFECT,
  ROLEPLAY_ASSESSMENT_DISPLAY,
  ROLEPLAY_SESSION_ASSESSMENT_DISPLAY,
  ROLEPLAY_HISTORY_ASSESSMENT_DISPLAY,
} from './services/roleplay/addon/roleplay-addon.tokens'
import { RoleplaySoundEffectService } from './services/roleplay/addon/roleplay-sound-effect.service'
import { RoleplayAssessmentDisplayComponent } from './addon/roleplay/default/roleplay-assessment-display.component'
import { RoleplaySimpleAssessmentComponent } from './addon/roleplay/simple/history/roleplay-simple-assessment.component'
import { MeetingRecordingPageComponent } from './pages/meeting-recording-page/meeting-recording-page.component'
import { trygroupDashboardGuard } from './guards/trygroup-dashboard.guard'
import { trygroupMemberAnalysisGuard } from './guards/trygroup-member-analysis.guard'
import {
  kpiDashboardGuard,
  kpiOperatorDetailGuard,
} from './guards/kpi-dashboard.guard'
import { TrygroupDashboardShellComponent } from './pages/trygroup-dashboard/trygroup-dashboard-shell.component'
import { TrygroupPriorityListPageComponent } from './pages/trygroup-dashboard/priority-list/trygroup-priority-list-page.component'
import { TrygroupMemberAnalysisPageComponent } from './pages/trygroup-dashboard/member-analysis/trygroup-member-analysis-page.component'
import { TrygroupSettingsShellComponent } from './pages/trygroup-dashboard/settings/trygroup-settings-shell.component'
import { TrygroupAccessGrantsSettingsPageComponent } from './pages/trygroup-dashboard/settings/access-grants/trygroup-access-grants-settings-page.component'
import { TrygroupMonthlySalesPerformanceImportSettingsPageComponent } from './pages/trygroup-dashboard/settings/monthly-sales-performance-import/trygroup-monthly-sales-performance-import-settings-page.component'
import { TrygroupExportSettingsPageComponent } from './pages/trygroup-dashboard/settings/export/trygroup-export-settings-page.component'
import { KpiDashboardSettingsComponent } from './pages/kpi-dashboard/settings/kpi-dashboard-settings.component'
import { KpiDashboardListComponent } from './pages/kpi-dashboard/dashboard-list/kpi-dashboard-list.component'
import { KpiOperatorListComponent } from './pages/kpi-dashboard/operator-list/kpi-operator-list.component'
import { KpiOperatorDetailComponent } from './pages/kpi-dashboard/operator-detail/kpi-operator-detail.component'

/**
 * Angular 22 で paramsInheritanceStrategy の既定が 'always' になり、子ルートが親の data を
 * 常に引き継ぐようになった。親ルートの data.roles（全ロール）を子が継承すると、data.roles を
 * 書き忘れた roleGuard 付きルートが全ロールに開放されるため、v21 までの 'emptyOnly' を維持する。
 */
export const routerConfig: RouterConfigOptions = {
  paramsInheritanceStrategy: 'emptyOnly',
}

export const routes: Routes = [
  {
    path: 'settings',
    component: MainPageComponent,
    canActivate: [authGuard, roleGuard],
    data: { roles: ['SUPER_ADMIN', 'ENABLER', 'MANAGER', 'USER'] },
    children: [
      { path: 'security', component: MfaSettingsPageComponent },
      { path: 'mfa', redirectTo: 'security' },
    ],
  },
  {
    path: 'login',
    component: LoginPageComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordPageComponent,
  },
  {
    path: 'no-license',
    component: NoLicensePageComponent,
  },
  {
    path: 'auth/action',
    component: AuthActionComponent,
  },
  {
    path: 'auth/salesforce/callback',
    component: SalesforceAuthComponent,
  },
  {
    path: 'auth/passkey-setup',
    component: PasskeySetupPageComponent,
  },
  {
    path: 'auth/passkey-verify',
    component: PasskeyVerifyPageComponent,
  },
  {
    path: 'auth/totp-setup',
    component: MfaTotpSetupPageComponent,
  },
  {
    path: 'auth/mfa-select',
    component: MfaMethodSelectPageComponent,
  },
  {
    path: 'auth/mfa-verify-select',
    component: MfaVerifySelectPageComponent,
  },
  {
    path: 'auth/totp-verify',
    component: TotpVerificationPageComponent,
  },
  {
    path: 'auth/phone-setup',
    component: MfaPhoneSetupPageComponent,
  },
  {
    path: 'auth/phone-setup/verify',
    component: SmsVerificationPageComponent,
  },
  {
    path: '',
    component: MainPageComponent,
    canActivate: [authGuard, roleGuard, licenseGuard],
    data: { roles: ['SUPER_ADMIN', 'ENABLER', 'MANAGER', 'USER'] },
    children: [
      {
        path: 'home',
        component: HomeComponent,
      },
      {
        path: 'users',
        component: UserListPageComponent,
      },
      {
        path: 'assessments',
        children: [
          {
            path: '',
            component: AssessmentListPageComponent,
          },
          {
            path: 'bulk-execution',
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
            component: AssessmentBulkExecutionComponent,
          },
          {
            path: 'meeting-recording',
            component: MeetingRecordingPageComponent,
            canActivate: [meetingRecordingGuard],
            canDeactivate: [
              (component: MeetingRecordingPageComponent) =>
                component.canDeactivate(),
            ],
          },
        ],
      },
      {
        path: 'admin',
        children: [
          {
            path: 'users',
            component: AdminUserListComponent,
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
          },
          {
            path: 'license-usage',
            component: LicenseUsagePageComponent,
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
          },
          {
            path: 'organization',
            component: OrganizationSettingPageComponent,
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN'] },
          },
          {
            path: 'assessment-settings',
            component: AssessmentSettingsPageComponent,
            canActivate: [roleGuard, assessmentSettingsGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
          },
          {
            path: 'external-integration',
            component: ExternalIntegrationPageComponent,
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
          },
        ],
      },
      {
        path: 'dashboard',
        children: [
          {
            path: 'individual',
            component: IndividualComponent,
          },
          {
            path: 'organization',
            component: OrganizationComponent,
          },
        ],
      },
      {
        path: 'kpi-dashboards/:dashboardId/settings',
        component: KpiDashboardSettingsComponent,
        canActivate: [roleGuard, kpiDashboardGuard],
        data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
      },
      {
        path: 'kpi-dashboards',
        component: KpiDashboardListComponent,
        canActivate: [kpiDashboardGuard],
      },
      {
        // 回答フォーム廃止（SBL-179）に伴うブックマーク互換。将来削除可
        path: 'kpi-dashboards/:dashboardId/response-forms/:sheetId',
        redirectTo: 'kpi-dashboards/:dashboardId',
        pathMatch: 'full',
      },
      {
        path: 'kpi-dashboards/:dashboardId',
        component: KpiOperatorListComponent,
        canActivate: [kpiDashboardGuard],
      },
      {
        path: 'kpi-dashboards/:dashboardId/operators/:userId',
        component: KpiOperatorDetailComponent,
        canActivate: [kpiOperatorDetailGuard],
      },
      {
        path: 'roleplay',
        canActivate: [addonGuard],
        children: [
          {
            path: 'persona/list',
            component: PersonaListPageComponent,
            canActivate: [roleGuard],
            data: { roles: ['SUPER_ADMIN', 'ENABLER'] },
          },
          {
            // 低遅延の新経路(SBL-1004)。org フラグ ROLEPLAY_LOW_LATENCY が
            // 有効な場合のみマッチし、無効・解決失敗・legacyRoleplay=1(サーバー
            // 拒否によるフォールバック)では次の旧ページ定義へフォールスルーする
            path: 'session',
            canMatch: [roleplayStreamingGuard],
            loadComponent: () =>
              import('./pages/roleplay/streaming-session/streaming-session-page.component').then(
                (m) => m.StreamingSessionPageComponent,
              ),
            canDeactivate: [
              (component: StreamingSessionPageComponent) =>
                component.canDeactivate(),
            ],
          },
          {
            path: 'session',
            component: RoleplaySessionPageComponent,
            canDeactivate: [
              (component: RoleplaySessionPageComponent) =>
                component.canDeactivate(),
            ],
            providers: [
              {
                provide: ROLEPLAY_SOUND_EFFECT,
                useExisting: RoleplaySoundEffectService,
              },
              {
                provide: ROLEPLAY_SESSION_ASSESSMENT_DISPLAY,
                useValue: RoleplayAssessmentDisplayComponent,
              },
              {
                provide: ROLEPLAY_HISTORY_ASSESSMENT_DISPLAY,
                useValue: RoleplaySimpleAssessmentComponent,
              },
              // deprecated token: 後方互換性のため残存
              {
                provide: ROLEPLAY_ASSESSMENT_DISPLAY,
                useValue: RoleplayAssessmentDisplayComponent,
              },
            ],
          },
        ],
      },
      {
        path: 'trygroup-dashboard',
        component: TrygroupDashboardShellComponent,
        children: [
          {
            path: '',
            redirectTo: 'priority-list',
            pathMatch: 'full',
          },
          {
            path: 'priority-list',
            component: TrygroupPriorityListPageComponent,
            canActivate: [trygroupDashboardGuard],
          },
          {
            path: 'member/:userId',
            component: TrygroupMemberAnalysisPageComponent,
            canActivate: [trygroupMemberAnalysisGuard],
          },
          {
            path: 'settings',
            component: TrygroupSettingsShellComponent,
            canActivate: [trygroupDashboardGuard],
            children: [
              {
                path: '',
                redirectTo: 'monthly-sales-performance-import',
                pathMatch: 'full',
              },
              {
                path: 'monthly-sales-performance-import',
                component:
                  TrygroupMonthlySalesPerformanceImportSettingsPageComponent,
                canActivate: [roleGuard],
                data: { roles: ['SUPER_ADMIN'] },
              },
              {
                path: 'access-grants',
                component: TrygroupAccessGrantsSettingsPageComponent,
                canActivate: [roleGuard],
                data: { roles: ['SUPER_ADMIN'] },
              },
              {
                path: 'export',
                component: TrygroupExportSettingsPageComponent,
                canActivate: [roleGuard],
                data: { roles: ['SUPER_ADMIN'] },
              },
              {
                path: 'ng-analysis-export',
                redirectTo: 'export',
                pathMatch: 'full',
              },
            ],
          },
        ],
      },
      {
        // 旧 prefix からアクセスされたら新 prefix へ恒久リダイレクト
        // (既存ブックマーク互換のため、削除ではなく redirect で残す)
        path: 'trygroup/dashboard',
        redirectTo: 'trygroup-dashboard',
        pathMatch: 'prefix',
      },
    ],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
]
